import React from 'react';
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * Remote Cyncly UMD bundles keyed by the global they expose. Both are loaded
 * together (see `loadCynclyScripts`) because the configurator's runtime depends
 * on the viewer bundle being present — loading only one leaves the other's
 * shared state uninitialized.
 */
const CYNCLY_SCRIPTS = {
  SelectionViewer:
    'https://testcomponents-v1-deve.cyncly-content.net/debug/selectionViewerLib.js',
  ItemConfigurator:
    'https://clientcomponents-v1-deve.cyncly-content.net/lib/clientConfiguratorLib.js',
} as const;

type CynclyGlobal = keyof typeof CYNCLY_SCRIPTS;

/**
 * Fetch and evaluate a remote Cyncly UMD bundle. The bundle reads React from
 * globals and resolves its assets relative to `BASE_COMPONENT_URL`, both of
 * which are set here before the code is evaluated.
 */
const loadCynclyScript = async (url: string): Promise<void> => {
  const response = await fetch(url);
  const code = await response.text();

  (globalThis as any).react = React;
  (globalThis as any)['react-dom'] = ReactDOM;

  const baseUrl = url.replace(/[\w\d]+\.js$/, '');

  new Function(`
        var globalThis = this;
        globalThis.BASE_COMPONENT_URL = "${baseUrl}";
        ${code}
        `).apply(globalThis, []);
};

// Load every bundle exactly once, shared across components and re-mounts.
let scriptsPromise: Promise<void> | null = null;
const loadCynclyScripts = (): Promise<void> => {
  if (!scriptsPromise) {
    scriptsPromise = Promise.all(
      Object.values(CYNCLY_SCRIPTS).map(loadCynclyScript)
    ).then(() => undefined);
  }
  return scriptsPromise;
};

/**
 * Load the Cyncly bundles and return the component exposed on the global scope
 * (`SelectionViewer` or `ItemConfigurator`), or `null` until every bundle is
 * ready. Both bundles are loaded regardless of which component is requested, so
 * the widgets can be rendered together or individually without a load-order
 * race.
 */
export const useLoadKBFrComponent = <T = unknown>(
  globalName: CynclyGlobal
): T | null => {
  const [lib, setLib] = useState<T | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadCynclyScripts().then(() => {
      if (!cancelled) {
        // Use a functional update so React stores the component *function*
        // itself instead of invoking it as a state updater.
        setLib(() => ((globalThis as any)[globalName] as T) ?? null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [globalName]);

  return lib;
};
